import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import { getVisualAssetUrl } from "../../../api";
import { wordWrapPassage } from "../utils/passageLineExtract";

// Book scans arrive hard-wrapped at the printed column width (~50 chars), so on a wide
// screen the passage fills half the box and on a phone every line wraps. Line numbers
// must keep pointing at the source lines, so the text cannot be re-flowed — instead the
// type size is scaled until the longest line spans the available width.
const FIT_BASE_PX = 16;
const FIT_MIN = 0.85;
const FIT_MAX = 1.35;

let fitCanvas = null;

const measureLineWidth = (line) => {
  if (typeof document === "undefined") return 0;
  if (!fitCanvas) fitCanvas = document.createElement("canvas");
  const ctx = fitCanvas.getContext("2d");
  if (!ctx) return 0;
  ctx.font = `${FIT_BASE_PX}px Georgia, serif`;
  return ctx.measureText(line).width;
};

// Source documents are frequently Windows-1252 but get stored as if they were
// Latin-1, turning smart quotes/dashes into unassigned C1 control points
// (U+0092, U+0093…) that render as empty boxes.
const WINDOWS_1252_C1 = {
  0x80: "€", 0x82: "‚", 0x83: "ƒ", 0x84: "„", 0x85: "…", 0x86: "†", 0x87: "‡",
  0x88: "ˆ", 0x89: "‰", 0x8a: "Š", 0x8b: "‹", 0x8c: "Œ", 0x8e: "Ž", 0x91: "‘",
  0x92: "’", 0x93: "“", 0x94: "”", 0x95: "•", 0x96: "–", 0x97: "—", 0x98: "˜",
  0x99: "™", 0x9a: "š", 0x9b: "›", 0x9c: "œ", 0x9e: "ž", 0x9f: "Ÿ",
};

const normalizeWindows1252 = (text) =>
  text.replace(/[\u0080-\u009f]/g, (c) => WINDOWS_1252_C1[c.charCodeAt(0)] ?? "");

// Quill stores passage text as HTML. Convert to plain text with markdown
// bold/italic so passageLineToHtml can re-render it correctly.
const stripHtmlToPassageText = (html) => {
  if (!html || !/<[a-z]/i.test(html)) return html || "";
  return html
    .replace(/<\/p>/gi, "\n")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, "**$1**")
    .replace(/<b[^>]*>([\s\S]*?)<\/b>/gi, "**$1**")
    .replace(/<em[^>]*>([\s\S]*?)<\/em>/gi, "*$1*")
    .replace(/<i[^>]*>([\s\S]*?)<\/i>/gi, "*$1*")
    .replace(/<[^>]+>/g, "")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
};

// Minimal markdown → HTML for passage lines (bold, italic, images)
const passageLineToHtml = (line) => {
  // Escape HTML entities first (before injecting tags)
  let result = line
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  // Convert markdown images ![alt](url) → <img> (must run before inline formatting)
  result = result.replace(
    /!\[([^\]]*)\]\(([^)\s]+)\)/g,
    (_, alt, src) => {
      let resolvedSrc = src;
      const r2Match = src.match(/https?:\/\/[^"'\s>]*\.r2\.[^"'\s>/]+\/(.+)/);
      const proxyMatch = src.match(/https?:\/\/[^/"'\s>]+\/api\/proxy\/visual\/(.+)/);
      if (r2Match) resolvedSrc = getVisualAssetUrl(r2Match[1]);
      else if (proxyMatch) resolvedSrc = getVisualAssetUrl(proxyMatch[1]);
      return `<img src="${resolvedSrc}" alt="${alt}" style="display:block;max-width:100%;max-height:320px;width:auto;height:auto;border-radius:6px;border:1px solid #e0e0e0;margin:8px 0" onerror="this.style.display='none'">`;
    }
  );

  // Inline formatting (bold / italic)
  result = result
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/\*([^*]+)\*/g, "<em>$1</em>");

  return result;
};

// Parse line number references from a question text, e.g. "line 5", "lines 3–7", "lines 3 to 7"
export const parseLineRefs = (text) => {
  if (!text) return [];
  const nums = new Set();
  const rangeRe = /lines?\s+(\d+)\s*(?:[-–]|to)\s*(\d+)/gi;
  const singleRe = /lines?\s+(\d+)/gi;
  let m;
  while ((m = rangeRe.exec(text)) !== null) {
    for (let n = +m[1]; n <= +m[2]; n++) nums.add(n);
  }
  singleRe.lastIndex = 0;
  while ((m = singleRe.exec(text)) !== null) nums.add(+m[1]);
  return [...nums];
};

// Prose paragraphs reflow with the viewport, so a line number can't be assigned to a
// source line the way the hard-wrapped renderer does — the student's line 5 depends on
// how the text actually broke. Numbers are therefore measured off the rendered line
// boxes after layout and painted into the left gutter at the matching offsets.
const PROSE_NUMBER_EVERY = 5;

const ProsePassage = ({ paragraphs, fontSize, header, containerRef }) => {
  const bodyRef = useRef(null);
  const [marks, setMarks] = useState([]);
  // `paragraphs` is rebuilt on every parent render, so the effect keys off the text
  // itself rather than the array identity.
  const paragraphsKey = paragraphs.join("\n");

  useLayoutEffect(() => {
    const node = bodyRef.current;
    if (!node) return undefined;

    const measure = () => {
      const base = node.getBoundingClientRect();
      const tops = [];
      const range = document.createRange();
      node.querySelectorAll("[data-para]").forEach((para) => {
        // A range over the whole paragraph yields one rect per rendered line box;
        // inline <em>/<strong> can split a line into several rects, so near-equal
        // tops collapse into the single line they belong to.
        range.selectNodeContents(para);
        Array.from(range.getClientRects()).forEach((rect) => {
          if (!rect.height) return;
          if (tops.length === 0 || rect.top - tops[tops.length - 1] > 1) {
            tops.push(rect.top);
          }
        });
      });

      const next = tops
        .map((top, idx) => ({ num: idx + 1, top: top - base.top }))
        .filter((m) => m.num % PROSE_NUMBER_EVERY === 0);

      // The observer fires on every layout pass, so re-setting an equal array would
      // spin: new state reference → re-render → measure → set again.
      setMarks((prev) =>
        prev.length === next.length && prev.every((p, i) => p.num === next[i].num && p.top === next[i].top)
          ? prev
          : next
      );
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [paragraphsKey, fontSize]);

  return (
    <Box ref={containerRef}>
      {header}
      <Box sx={{ display: "flex", alignItems: "flex-start" }}>
        <Box sx={{ position: "relative", width: 28, flexShrink: 0, alignSelf: "stretch" }}>
          {marks.map((m) => (
            <Box
              key={m.num}
              sx={{
                position: "absolute",
                top: `${m.top}px`,
                right: 0,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                color: "#666",
                userSelect: "none",
                pointerEvents: "none",
              }}
            >
              {m.num}
            </Box>
          ))}
        </Box>
        <Box ref={bodyRef} sx={{ flex: 1, pl: 1.5, minWidth: 0 }}>
          {paragraphs.map((para, idx) => {
            const isDialogue = /^[“"‘']/.test(para);
            return (
              <Box
                key={idx}
                data-para=""
                sx={{
                  fontFamily: "Georgia, serif",
                  fontSize: `${fontSize.toFixed(3)}rem`,
                  overflowWrap: "break-word",
                  lineHeight: 1.85,
                  color: "#2a2a2a",
                  mb: 1.5,
                  textIndent: isDialogue ? 0 : "1.5em",
                }}
                dangerouslySetInnerHTML={{ __html: passageLineToHtml(para) }}
              />
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

/**
 * PassageWithLineNumbers
 *
 * Renders a reading passage with LEFT-margin line numbers (GL Assessment style).
 *
 * Features:
 * - Detects and renders heading section from "### Title **by Author**" markdown lines
 * - Renders full-line italic blocks "*...*" as a bordered context/summary box
 * - Supports {Ln} markers (e.g. {L5}, {L10}) for explicit line numbers
 * - Supports "1. text" paragraph prefix as an explicit line 1 anchor
 * - Sequential fallback: shows number every 5th non-empty line when no markers present
 *   (set showAllFallbackLineNumbers for admin review so every row has a number)
 *
 * Props:
 *   text          – passage text (may contain ### headings, *italic blocks*, {Ln} markers)
 *   highlightLines – array of line numbers to accent (matched from sub-question refs)
 *   showAllFallbackLineNumbers – when true and no {L…} markers, show a number on every content row
 *   prose         – book-scan mode: flowing paragraphs with first-line indent, no fitWidth
 *                   column, no decorative line numbers (when questions do not cite "line N")
 */
const PassageWithLineNumbers = ({
  text,
  highlightLines = [],
  showAllFallbackLineNumbers = false,
  wrapWidth,
  fontSize = 1.0,
  fitWidth = false,
  // Book-scan worksheets: flowing paragraphs with first-line indent, no column
  // hard-wraps and no decorative line numbers (unless questions cite "line N").
  prose = false,
}) => {
  const highlightSet = new Set(highlightLines.map(Number));
  const fitRef = useRef(null);
  const [fitScale, setFitScale] = useState(1);
  const [fitMaxWidth, setFitMaxWidth] = useState(null);
  const useFitWidth = fitWidth && !prose;

  // Convert non-breaking spaces (U+00A0 / &nbsp;) to regular spaces. Imported passages
  // store every space as &nbsp;, which blocks word-wrapping (forcing mid-word breaks)
  // and defeats wordWrapPassage's space-based splitting.
  const normalizedText = normalizeWindows1252(stripHtmlToPassageText(text || ""))
    .replace(/ /g, " ")
    .replace(/&nbsp;/gi, " ");

  const rawLines = normalizedText.split("\n");

  // ── Separate the header block (title, author, context box) from the passage body ──
  const headerItems = []; // { type: 'heading' | 'context', raw: string }
  let bodyStartIdx = 0;
  let i = 0;

  while (i < rawLines.length) {
    const trimmed = rawLines[i].trim();

    if (trimmed === "") {
      // Skip blank lines in the header area
      i++;
      bodyStartIdx = i;
      continue;
    }

    if (/^#{1,6}\s/.test(trimmed)) {
      // Markdown heading line
      headerItems.push({ type: "heading", raw: trimmed });
      i++;
      bodyStartIdx = i;
      continue;
    }

    // Full-line italic block: starts with * (not **) and ends with * (not **), ≥30 chars
    if (
      /^\*[^*]/.test(trimmed) &&
      trimmed.endsWith("*") &&
      !trimmed.endsWith("**") &&
      trimmed.length > 30
    ) {
      headerItems.push({ type: "context", raw: trimmed });
      i++;
      bodyStartIdx = i;
      continue;
    }

    // First non-header content line — passage body starts here
    break;
  }

  // Word-wrap flat passages (no newlines) so line numbers correspond to exam paper layout.
  const bodyText = wordWrapPassage(rawLines.slice(bodyStartIdx).join("\n"), wrapWidth);

  // Pre-process: convert "1. text" paragraph-start prefix → "{L1}text"
  // so the first line's anchor is shown in the left margin.
  const bodyProcessed = bodyText.replace(/^(\d+)\.\s/gm, (_, n) => `{L${n}}`);

  const hasMarkers = /\{L\d+\}/.test(bodyProcessed);

  const processed = (() => {
    if (!hasMarkers) {
      let count = 0;
      return bodyProcessed.split("\n").map((line) => {
        const hasContent = line.trim().length > 0;
        const isImageLine = /^!\[[^\]]*\]\([^)]+\)\s*$/.test(line.trim());
        if (hasContent && !isImageLine) count++;
        return {
          html: hasContent ? passageLineToHtml(line) : null,
          num: hasContent && !isImageLine ? count : null,
          explicitNum: null,
        };
      });
    }

    const result = [];
    bodyProcessed.split("\n").forEach((rawLine) => {
      // Split at each {L#} marker so mid-line markers (caused by Quill collapsing \n
      // into one paragraph) each create their own numbered row instead of being lost.
      const segs = rawLine.split(/(?=\{L\d+\})/);
      segs.forEach((seg) => {
        if (!seg) return;
        const mMatch = seg.match(/^\{L(\d+)\}/);
        const lineNum = mMatch ? parseInt(mMatch[1], 10) : null;
        const cleanSeg = seg.replace(/\{L\d+\}/g, "").trimEnd();
        const hasContent = cleanSeg.trim().length > 0;
        if (hasContent || lineNum !== null) {
          result.push({
            html: hasContent ? passageLineToHtml(cleanSeg) : null,
            num: null,
            explicitNum: lineNum,
          });
        }
      });
    });
    return result;
  })();

  // Widest source line (measured, not counted — Georgia is proportional).
  const widestLineWidth = useMemo(() => {
    if (!useFitWidth) return 0;
    return bodyProcessed.split("\n").reduce((widest, line) => {
      const plain = line.replace(/\{L\d+\}/g, "").replace(/\*/g, "").trim();
      if (!plain) return widest;
      return Math.max(widest, measureLineWidth(plain));
    }, 0);
  }, [useFitWidth, bodyProcessed]);

  useLayoutEffect(() => {
    if (!useFitWidth) {
      setFitScale(1);
      setFitMaxWidth(null);
      return undefined;
    }
    const node = fitRef.current;
    if (!node || !widestLineWidth) return undefined;

    const measure = () => {
      // 28px number gutter + 12px gutter padding + 4px breathing room.
      const available = node.clientWidth - 44;
      if (available <= 0) return;
      const raw = available / widestLineWidth;
      const scale = Math.min(FIT_MAX, Math.max(FIT_MIN, raw));
      setFitScale(scale);
      // Past the largest comfortable type size the leftover width is centred, so a
      // short-lined book scan reads as a column instead of hugging the left edge.
      setFitMaxWidth(raw > FIT_MAX ? Math.ceil(widestLineWidth * scale) + 44 : null);
    };

    measure();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [useFitWidth, widestLineWidth]);

  const effectiveFontSize = fontSize * (useFitWidth ? fitScale : 1);

  // ── Render the header section (book title, author line, context box) ──
  const renderHeader = () => {
    if (headerItems.length === 0) return null;
    return (
      <Box sx={{ mb: 1.5 }}>
        {headerItems.map((item, idx) => {
          if (item.type === "heading") {
            // Strip # prefix: "### The Swiss Family Robinson **by Johann David Wyss**"
            const headingContent = item.raw.replace(/^#{1,6}\s+/, "");
            // Split on **bold** segments to separate title from author attribution
            const segments = headingContent
              .split(/(\*\*[^*]+\*\*)/g)
              .filter(Boolean);
            return (
              <Box key={idx} sx={{ textAlign: "center", mb: 0.25 }}>
                {segments.map((seg, si) => {
                  const isBold = seg.startsWith("**") && seg.endsWith("**");
                  const segText = isBold ? seg.slice(2, -2) : seg.trim();
                  if (!segText) return null;
                  return (
                    <Typography
                      key={si}
                      sx={{
                        display: "block",
                        fontFamily: "Georgia, serif",
                        // Non-bold = book title (large, bold)
                        // Bold = author attribution (smaller, italic, lighter)
                        fontWeight: isBold ? 400 : 700,
                        fontStyle: isBold ? "italic" : "normal",
                        fontSize: isBold ? "1.0rem" : "1.25rem",
                        color: "#1a1a1a",
                        lineHeight: 1.4,
                      }}
                    >
                      {segText}
                    </Typography>
                  );
                })}
              </Box>
            );
          }

          if (item.type === "context") {
            // Strip surrounding * and render as a bordered context/summary box
            const contextText = item.raw.slice(1, -1);
            return (
              <Box
                key={idx}
                sx={{
                  border: "1px solid #bbb",
                  borderRadius: 0.5,
                  p: 1.5,
                  my: 1.5,
                  bgcolor: "#fefefe",
                }}
              >
                <Box
                  sx={{
                    fontStyle: "italic",
                    fontSize: `${(effectiveFontSize * 0.88).toFixed(2)}rem`,
                    color: "#333",
                    lineHeight: 1.75,
                    fontFamily: "Georgia, serif",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: passageLineToHtml(contextText),
                  }}
                />
              </Box>
            );
          }

          return null;
        })}
      </Box>
    );
  };

  if (prose && !hasMarkers) {
    const paragraphs = bodyProcessed
      .split(/\n+/)
      .map((l) => l.replace(/\{L\d+\}/g, "").trim())
      .filter(Boolean);

    return (
      <ProsePassage
        paragraphs={paragraphs}
        fontSize={effectiveFontSize}
        header={renderHeader()}
        containerRef={fitRef}
      />
    );
  }

  return (
    <Box ref={fitRef}>
      <Box sx={fitMaxWidth ? { maxWidth: `${fitMaxWidth}px`, mx: "auto" } : undefined}>
      {renderHeader()}
      {processed.map((item, idx) => {
        const lineNumForHighlight = hasMarkers ? item.explicitNum : item.num;
        const isHighlighted =
          lineNumForHighlight != null && highlightSet.has(lineNumForHighlight);
        const showNum = hasMarkers
          ? item.explicitNum != null
          : item.num != null &&
            (showAllFallbackLineNumbers || item.num % 5 === 0 || isHighlighted);
        const displayNum = hasMarkers ? item.explicitNum : item.num;

        return (
          <Box
            key={idx}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              minHeight: item.html ? undefined : "0.6em",
              ...(isHighlighted ? { bgcolor: "#fff3e0" } : {}),
            }}
          >
            <Box
              sx={{
                minWidth: 28,
                textAlign: "right",
                pr: 1.5,
                fontSize: "0.72rem",
                fontFamily: "monospace",
                lineHeight: 1.9,
                flexShrink: 0,
                userSelect: "none",
                pt: "3px",
                color: isHighlighted ? "#e65100" : "#666",
                fontWeight: isHighlighted ? 700 : 400,
              }}
            >
              {showNum ? displayNum : ""}
            </Box>
            <Box
              sx={{
                flex: 1,
                fontFamily: "Georgia, serif",
                fontSize: `${effectiveFontSize.toFixed(3)}rem`,
                overflowWrap: "break-word",
                lineHeight: 1.9,
                color: "#2a2a2a",
                px: isHighlighted ? 0.5 : 0,
              }}
              dangerouslySetInnerHTML={{ __html: item.html || "" }}
            />
          </Box>
        );
      })}
      </Box>
    </Box>
  );
};

export default PassageWithLineNumbers;
